import { Button } from '@base-ui/react/button';
import { faFileLines } from '@fortawesome/free-solid-svg-icons';
import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import styled from 'styled-components';
import api, { queryClient } from '@/api';
import type { components } from '@/api/qraft/schema';
import { ConfirmDialog } from '~/components/ui/ConfirmDialog';
import { SeriesHeader } from '~/components/ui/ContentNav';
import {
  DetailPage,
  EditorLayout,
  EditorMain,
  EditorPanel,
  FormSection,
  PanelGroup,
  PanelLabel,
  SaveGroup,
} from '~/components/ui/Editor';
import { useEditMode, validateEditSearch } from '~/components/ui/editSearch';
import { Field, FieldLabel, FieldValue } from '~/components/ui/FieldDisplay';
import {
  ErrorText,
  FieldHint,
  FormLabel,
  FormRow,
  InfoNotice,
  Select,
  TextInput,
  WarningNotice,
} from '~/components/ui/Form';
import { FieldGrid, Section, SectionTitle } from '~/components/ui/Section';
import { useUnsavedChanges } from '~/components/ui/useUnsavedChanges';
import { LANGUAGE_LABELS } from '~/constants/languages';
import buttonStyles from '~/styles/button.module.css';
import { CertificateTemplateControl } from './CertificateTemplateControl';

type SeriesAdmin = components['schemas']['SeriesAdmin'];

function invalidateSeries(seriesId: number) {
  queryClient.invalidateQueries({
    queryKey: api.adminGetSeries.getQueryKey({ path: { series_id: seriesId } }),
  });
  queryClient.invalidateQueries({
    queryKey: api.adminListSeries.getQueryKey({}),
  });
}

export const Route = createFileRoute('/_admin/series/$seriesId')({
  component: SeriesDetail,
  validateSearch: validateEditSearch,
});

function SeriesDetail() {
  const { seriesId } = Route.useParams();
  const seriesIdNum = Number(seriesId);
  const { edit } = Route.useSearch();
  const { editing, startEditing, stopEditing } = useEditMode(edit === true);

  const admin = api.adminGetSeries.useQuery({
    path: { series_id: seriesIdNum },
  });

  if (admin.isLoading) return <Message>Loading series...</Message>;
  if (admin.error || !admin.data)
    return <Message $error>Error loading series</Message>;

  const series = admin.data;
  const certificateTitle = series.certificate_template_title;

  return (
    <DetailPage>
      <SeriesHeader
        series={series}
        nav={!editing}
        action={
          !editing && (
            <Button className={buttonStyles.Button} onClick={startEditing}>
              Edit
            </Button>
          )
        }
      />

      {editing ? (
        <SeriesEditor key={series.id} series={series} onDone={stopEditing} />
      ) : (
        <>
          <Section>
            <SectionTitle>Overview</SectionTitle>
            <FieldGrid>
              <Field>
                <FieldLabel>ID</FieldLabel>
                <FieldValue>{series.id}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Language</FieldLabel>
                <FieldValue>{LANGUAGE_LABELS[series.language]}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Slug</FieldLabel>
                <FieldValue>{series.slug || '—'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Order</FieldLabel>
                <FieldValue>{series.order}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Status</FieldLabel>
                <FieldValue>{series.status}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Self Paced</FieldLabel>
                <FieldValue>{series.is_self_paced ? 'Yes' : 'No'}</FieldValue>
              </Field>
              <Field>
                <FieldLabel>Starter</FieldLabel>
                <FieldValue>{series.is_starter ? 'Yes' : 'No'}</FieldValue>
              </Field>
            </FieldGrid>
          </Section>

          <Section>
            <SectionTitle>Certificate</SectionTitle>
            <CertificateRow>
              <div>
                <FieldValue>
                  {certificateTitle !== null ? (
                    <>
                      Students who complete every course in this series earn{' '}
                      <CertificateName>{certificateTitle}</CertificateName>.
                    </>
                  ) : (
                    <>
                      No certificate. Finishing this series earns nothing,
                      however many of its courses a student completes.
                    </>
                  )}
                </FieldValue>
              </div>
              <CertificateTemplateControl
                owner={{ type: 'series', id: series.id }}
                hasTemplate={certificateTitle !== null}
                defaultTitle={series.title}
                onChanged={() => invalidateSeries(series.id)}
              />
            </CertificateRow>
          </Section>
        </>
      )}
    </DetailPage>
  );
}

function SeriesEditor({
  series,
  onDone,
}: {
  series: SeriesAdmin;
  onDone: () => void;
}) {
  const [title, setTitle] = useState(series.title);
  const [slug, setSlug] = useState(series.slug);
  const [status, setStatus] = useState(series.status);
  const [confirming, setConfirming] = useState(false);

  const formValues = JSON.stringify([title, slug, status]);
  const [savedValues] = useState(formValues);
  const dirty = formValues !== savedValues;
  const {
    allowLeave,
    confirmDiscard,
    dialog: unsavedDialog,
  } = useUnsavedChanges(dirty);

  const updateMutation = api.adminUpdateSeries.useMutation(undefined, {
    onSuccess: () => {
      allowLeave();
      invalidateSeries(series.id);
      onDone();
    },
  });

  const trimmedTitle = title.trim();
  // Publishing is one-way (the server rejects published -> draft), so an
  // already-published series shows no status control at all.
  const isPublished = series.status === 'published';
  const willPublish = !isPublished && status === 'published';

  const handleSave = () => {
    if (!trimmedTitle || updateMutation.isPending) return;
    if (willPublish && !confirming) {
      setConfirming(true);
      return;
    }
    setConfirming(false);
    updateMutation.mutate({
      path: { series_id: series.id },
      body: { title: trimmedTitle, slug, status },
    });
  };

  return (
    <EditorLayout>
      <EditorMain>
        <FormSection
          tone="content"
          icon={faFileLines}
          title="Details"
          description="How the series is named and linked."
        >
          <FormRow>
            <FormLabel htmlFor="series-title">Title</FormLabel>
            <TextInput
              id="series-title"
              value={title}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setTitle(e.target.value)
              }
            />
          </FormRow>
          <FormRow>
            <FormLabel htmlFor="series-slug">Slug</FormLabel>
            <TextInput
              id="series-slug"
              value={slug}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSlug(e.target.value)
              }
            />
          </FormRow>
        </FormSection>
      </EditorMain>

      <EditorPanel aria-label="Save and settings">
        <SaveGroup
          heading={<PanelLabel>Series</PanelLabel>}
          dirty={dirty}
          saving={updateMutation.isPending}
          saveDisabled={!trimmedTitle}
          onSave={handleSave}
          onCancel={() => confirmDiscard(onDone)}
        >
          {!trimmedTitle && <FieldHint>Give the series a title.</FieldHint>}
          {updateMutation.isError && (
            <ErrorText>Could not save changes. Please try again.</ErrorText>
          )}
        </SaveGroup>

        <PanelGroup>
          <FormRow>
            <FormLabel htmlFor="series-status">Status</FormLabel>
            {isPublished ? (
              <InfoNotice>
                Published and live for students. Publishing can't be undone, so
                there's nothing to change here.
              </InfoNotice>
            ) : (
              <>
                <Select
                  id="series-status"
                  value={status}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    setStatus(e.target.value as SeriesAdmin['status'])
                  }
                >
                  <option value="draft">draft</option>
                  <option value="published">published</option>
                </Select>
                {willPublish && (
                  <WarningNotice>
                    Publishing is permanent — a published series can't be
                    returned to draft, since that would remove it for students
                    who are partway through it. Its quizzes lock once students
                    can reach them: published questions can only have their
                    wording changed, and no questions can be added.
                  </WarningNotice>
                )}
              </>
            )}
          </FormRow>
        </PanelGroup>
      </EditorPanel>

      <ConfirmDialog
        open={confirming}
        title="This can't be undone"
        confirmLabel="Publish"
        onConfirm={handleSave}
        onCancel={() => setConfirming(false)}
      >
        <p>
          A published series can't be returned to draft, since that would remove
          it for students who are partway through it.
        </p>
        <p>
          Its quizzes lock once students can reach them: published questions can
          only have their wording changed, and no questions can be added.
        </p>
      </ConfirmDialog>
      {unsavedDialog}
    </EditorLayout>
  );
}

const CertificateName = styled.strong`
  color: var(--grey-900);
  font-weight: 600;
`;

const CertificateRow = styled.div`
  align-items: center;
  display: flex;
  gap: var(--component-margin);
  justify-content: space-between;
  padding: var(--inner-padding);
`;

const Message = styled.div<{ $error?: boolean }>`
  color: ${(p) => (p.$error ? 'var(--error)' : 'var(--grey-600)')};
  padding: 2rem;
  text-align: center;
`;
